import React, {useEffect, useState} from 'react'
import Chart from "react-apexcharts";
import {FutureCalc, IAssetDetail, IFutureYear} from "../../../../components/calculator/Calculator";
import {numberToMoneyFormat} from "../../../../stores/StoreUtilities";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  TableContainer,
  Tooltip,
  Typography,
} from "@mui/material";
import cloneDeep from "lodash.clonedeep";
import TextFieldValidator from "../../../../components/form/TextFieldValidator";
import FormValidator from "../../../../components/form/FormValidator";
import Model, {ModelSetting} from "../../../../model/Model";
import {useResources} from "../../../../stores/ResourceProvider";
import {IWithdrawalGroup, WithdrawalStrategyType} from "../../../../model/WithdrawalStrategy";
import Plan from "../../../../model/Plan";
import PlanChange, {PlanChangeType} from "../../../../model/PlanChange";
import WithdrawalStrategyChange from "../../../../model/changes/WithdrawalStrategyChange";
import Asset from "../../../../model/Asset";
import {globalColors} from "../../../../styles/globalStyles";
import WithdrawalStrategyDialog from "../../../../pages/calc/optimizer/WithdrawalStrategyDialog";
import {UpdatePlanInput} from '../../../../API';
import InfoIcon from '@mui/icons-material/Info';

const defaultOptions = {
  chart: {
    id: "budget-projection",
    type: 'bar',
    stacked: true,
    height: 350,
    width: "100%",
    // zoom: {
    //   autoScaleYaxis: true
    // },
    events: {
      mounted: (chart: any) => {
        chart.windowResizeHandler();
      }
    }
  },
  plotOptions: {
    bar: {
      columnWidth: "90%",
      dataLabels: {
        enabled: true,
        textAnchor: "middle",
        position: 'top',
        offsetY: -28,
        style: {
          fontSize: '16px',
          colors: ["rgb(51, 51, 51)"]
        },
        hideOverflowingLabels: false,
        total: {
          enabled: false,
          formatter: (value: number) => {
            return numberToMoneyFormat(value, 0)
          },
        }
      }
    }
  },
  title: {
    text: "",
    align: "center",
    margin: 0,
    style: {
      fontSize: "16px",
      fontFamily: "Poppins,sans-serif",
      color: "rgb(31, 92, 167)"
    }
  },
  dataLabels: {
    enabled: true,
    textAnchor: "middle",
    position: 'top',
    offsetY: -28,
    style: {
      fontSize: '16px',
      colors: ["rgb(51, 51, 51)"]
    },
    hideOverflowingLabels: false,
    formatter: (value: number, options: any) => {
      // return numberToMoneyFormat(value, 0)
      let topIndex = options.globals.series.length - 1
      while (topIndex >= 0 && options.globals.series[topIndex][options.dataPointIndex] === 0) {
        --topIndex
      }
      if (options.seriesIndex === 0 || options.seriesIndex === topIndex) {
        const group = options.seriesIndex === 0 ? 0 : 1
        const amount = options.globals.stackedSeriesTotalsByGroups[group][options.dataPointIndex] ?? 0
        return numberToMoneyFormat(amount, 0);
      } else {
        return "";
      }
    }
  },
  legend: {
    position: 'top', // TODO: Figure out why this isn't working
    horizontalAlign: 'left'
  },
  xaxis: {
    categories: ['Income', 'Conservative', 'Moderate', 'Growth'],
    axisBorder: {
      show: true
    },
    labels: {
      style: {
        fontSize: '20px',
        fontWeight: 600,
        colors: ["rgb(31, 92, 167)", "rgb(31, 92, 167)", "rgb(31, 92, 167)", "rgb(31, 92, 167)"]
      },
      offsetY: 0
    }
  },
  yaxis: {
    labels: {
      formatter: (value: number) => {
        return numberToMoneyFormat(value, 0)
      }
    }
  },
  tooltip: {
    y: {
      formatter: function (value: number) {
        return numberToMoneyFormat(value, 0)
      }
    }
  },
  // tooltip: {
  //   x: {
  //     format: 'dd MMM yyyy'
  //   }
  // },
  colors: [...globalColors.categorical],
  // fill: {
  //   type: 'gradient',
  //   gradient: {
  //     shadeIntensity: 1,
  //     opacityFrom: 0.7,
  //     opacityTo: 0.9,
  //     stops: [0, 100]
  //   }
  // },
  // stroke: {
  //   width: 3
  // },
  resonsive: [{
    breakpoint: undefined,
    options: {}
  }]
}

const BucketsChart = ({
  futureCalcs,
  onChange
}: {
  futureCalcs: FutureCalc[]
  onChange?: (plan: Plan) => any
}) => {
  const [options, setOptions] = useState<any>()
  const [series, setSeries] = useState<any>()
  const [model, setModel] = useState<Model | undefined>()
  const [plan, setPlan] = useState<Plan | undefined>()
  const [yearOption, setYearOption] = useState<number>(0)
  const [yearOptions, setYearOptions] = useState<number[]>([])
  const [incomeYears, setIncomeYears] = useState<number>(3)
  const [conservativeYears, setConservativeYears] = useState<number>(3)
  const [moderateYears, setModerateYears] = useState<number>(3)
  const [withdrawalStrategyChange, setWithdrawalStrategyChange] = useState<WithdrawalStrategyChange | undefined>()
  const [withdrawalStrategyType, setWithdrawalStrategyType] = useState<WithdrawalStrategyType | undefined>()
  const [adjustWithdrawals, setAdjustWithdrawals] = useState<boolean>(false)
  const [showWithdrawalStrategyDialog, setShowWithdrawalStrategyDialog] = useState<boolean>(false)

  // const [bucketGroups, setBucketGroups] = useState<IWithdrawalGroup[]>([])
  const { modelStore } = useResources()

  useEffect(() => {
    if (futureCalcs.length > 0) {
      const futureCalc = futureCalcs[0]
      let startYear = Math.max(futureCalc.latestRetirementYear, futureCalc.startYear)
      let endYear = futureCalc.latestLifeExpectancyYear
      if (futureCalc.model) {
        setModel(futureCalc.model)
        setPlan(futureCalc.plan) 
        const bucketsStartYear = futureCalc.model.settings[ModelSetting.BucketsStartYear]
        if (bucketsStartYear > 0) {
          startYear = Math.max(bucketsStartYear, futureCalc.startYear)
        }
        setYearOption(startYear)
        const bucketsIncomeYears = futureCalc.model.settings[ModelSetting.BucketsIncomeYears]
        if (bucketsIncomeYears >= 0) {
          setIncomeYears(bucketsIncomeYears)
        }
        const bucketsConservativeYears = futureCalc.model.settings[ModelSetting.BucketsConservativeYears]
        if (bucketsConservativeYears >= 0) {
          setConservativeYears(bucketsConservativeYears)
        }
        const bucketsModerateYears = futureCalc.model.settings[ModelSetting.BucketsModerateYears]
        if (bucketsModerateYears >= 0) {
          setModerateYears(bucketsModerateYears)
        }

        const bucketsBalance = futureCalc.model.settings[ModelSetting.BucketsAdjustWithdrawals]
        if (bucketsBalance === true) {
          setAdjustWithdrawals(bucketsBalance)
        }

        const change : WithdrawalStrategyChange | undefined = futureCalc.plan ? futureCalc.plan.getChange(PlanChangeType.WithdrawalStrategy) as WithdrawalStrategyChange : undefined
        if (change && change.enabled) {
          setWithdrawalStrategyChange(change)
          const strategy = change._withdrawalStrategy.withdrawalStrategyType
          setWithdrawalStrategyType(strategy)
        } else {
          setWithdrawalStrategyType(undefined)
        }
      }
      const yrs = range(futureCalc.startYear, endYear)
      setYearOptions(yrs)
    }
  }, [futureCalcs])

  useEffect(() => {
    if (yearOption > 0) {
      calculateData(futureCalcs)
    }
  }, [yearOption, incomeYears, conservativeYears, moderateYears, adjustWithdrawals, withdrawalStrategyType, withdrawalStrategyChange])

  const range = (start: number, end: number) => Array.from({length: (end - start)}, (v, k) => k + start);

  const fillBucket = (array: number[], start: number, count: number, capital: number) => {
    let shortfall = 0;
    if (count > 0) {
      let end = start + count - 1
      return [
        array.slice(start - 1, end).reduce(function (s, c) {
          [s, c] = [Math.max(Number(s), 0), Math.min(Number(c), 0)];
          s += c;
          c -= Math.min(s, 0);
          shortfall -= c;
          return s;
        }, capital), shortfall];
    } else {
      return [capital, 0]
    }
  };

  function getAssetSeries(assets: Asset[], fc: FutureCalc, startYear: number, buckets: [number, number, number][], bucketGroups?: IWithdrawalGroup[]) {
    const seriesArray: any[] = []
    let years = fc.futureYears.length
    const yearOffset = startYear - fc.startYear

    assets.forEach((asset: Asset) => {
      const series = {
        name: asset.description,
        group: "Assets",
        data: new Array<number>(buckets.length).fill(0)
      }
      const year = Math.min(yearOffset, years - 1)
      const fy : IFutureYear = fc.futureYears[year]
      buckets.forEach((bucket, index) => {
        const detail = fy.assetDetails.find((ad: IAssetDetail) => ad.liquidAsset.id === asset.id)
        if (detail) {
          if (bucketGroups) {
            const group = bucketGroups.findIndex((g: IWithdrawalGroup) => {
              return g.assetIds.includes(asset.id)
            })
            if (group === index) {
              series.data[group] += detail.boyBalance
            } else {
              // series.data[index] += detail.boyBalance
            }
          } else {
            series.data[index] += detail.boyBalance
          }
        }
      })
      seriesArray.push(series)
    })

    return seriesArray
  }

  function getAssetSeriesAdjusted(assets: Asset[], fc: FutureCalc, startYear: number, buckets: [number, number, number][], bucketGroups?: IWithdrawalGroup[]) {
    const seriesArray: any[] = []
    let years = fc.futureYears.length
    const yearOffset = startYear - fc.startYear
    const year = Math.min(yearOffset, years - 1)
    const fy : IFutureYear = fc.futureYears[year]
    const assetDetails = cloneDeep(fy.assetDetails)

    assets.forEach((asset: Asset) => {
      const detail = assetDetails.find((ad: IAssetDetail) => ad.liquidAsset.id === asset.id)
      if (detail && detail.boyBalance > 0) {
        const series = {
          name: asset.description,
          group: "Assets",
          data: new Array<number>(buckets.length).fill(0)
        }
        buckets.forEach((bucket, index) => {
          const needed = bucket[2]
          if (needed > 0) {
            if (detail.boyBalance > 0) {
              const amount = Math.min(needed, detail.boyBalance)
              series.data[index] += amount
              detail.boyBalance -= amount
              bucket[2] -= amount
            }
          }
        })
        seriesArray.push(series)
      }
    })

    return seriesArray
  }
/*
  function getAssetSeriesProjected(assets: Asset[], fc: FutureCalc, startYear: number, buckets: [number, number, number][], bucketGroups?: IWithdrawalGroup[]) {
    const seriesArray: any[] = []
    let years = fc.futureYears.length
    const yearOffset = startYear - fc.startYear - 1

    assets.forEach((asset: Asset) => {
      const series = {
        name: asset.description,
        group: "Assets",
        data: new Array<number>(buckets.length).fill(0)
      }
      buckets.forEach((bucket, index) => {
        const start = Math.min(bucket[0] + yearOffset, years - 1)
        const end = Math.min(bucket[0] + bucket[1] - 1 + yearOffset, years - 1)
        for (let year = start; year <= end; year++) {
          const fy : IFutureYear = fc.futureYears[year]
          const detail = fy.assetDetails.find((ad: IAssetDetail) => ad.liquidAsset.id === asset.id)
          if (detail) {
            if (bucketGroups) {
              const group = bucketGroups.findIndex((g: IWithdrawalGroup) => {
                return g.assetIds.includes(asset.id)
              })
              if (group > -1) {
                series.data[group] += -detail.distribution + detail.rmd
              } else {
                series.data[index] += -detail.distribution + detail.rmd
              }
            } else {
              series.data[index] += -detail.distribution + detail.rmd
              if (year === years - 1) {
                series.data[index] += detail.eoyBalance
              }
            }
          }
        }
      })
      seriesArray.push(series)
    })

    return seriesArray
  }
*/
  const calculateData = (calcs: FutureCalc[]) => {
    if (calcs.length === 0) {
      return
    }
    const newOptions = cloneDeep(defaultOptions)
    let seriesData: any[] = []

    futureCalcs.forEach((fc: FutureCalc) => {
      let firstYear
      if (yearOption) {
        firstYear = yearOption
      } else {
        firstYear = fc.latestRetirementYear
      }
      let start = Math.max(firstYear - fc.startYear, 0)

      let isBucketStrategy = false
      let bucketGroups : IWithdrawalGroup[] = []
      const withdrawalStrategyChange : WithdrawalStrategyChange | undefined = fc.plan ? fc.plan.getChange(PlanChangeType.WithdrawalStrategy) as WithdrawalStrategyChange : undefined
      if (withdrawalStrategyChange && withdrawalStrategyChange.enabled /* &&
        withdrawalStrategyChange._withdrawalStrategy.withdrawalStrategyType === WithdrawalStrategyType.Bucket */) {
        bucketGroups = withdrawalStrategyChange._withdrawalStrategy.withdrawalGroups
        isBucketStrategy = withdrawalStrategyChange._withdrawalStrategy.withdrawalStrategyType === WithdrawalStrategyType.Bucket
        setWithdrawalStrategyType(withdrawalStrategyChange._withdrawalStrategy.withdrawalStrategyType)
      }

      let workingCapital = fc.futureYears[start].currentLiquidAssets
      let years = new Array<number>(10)
      // for (let i = 0; i < 10 && start < fc.endYear; i++) {
      for (let i = start; i < fc.futureYears.length; i++) {
        const year = fc.futureYears[i]
        // Ignore surplus amounts
        years[i-start] = Math.min(year.surplus, 0) - year.rmds
      }

      let income;
      [workingCapital, income] = fillBucket(years, 1, incomeYears, workingCapital);

      let conservativeStart = incomeYears + 1
      let conservative;
      [workingCapital, conservative] = fillBucket(years, conservativeStart, conservativeYears, workingCapital);

      let moderateStart = conservativeStart + conservativeYears
      let moderate;
      [workingCapital, moderate] = fillBucket(years, moderateStart, moderateYears, workingCapital);

      const remainingCapital = Math.max(workingCapital, 0);

      let growthStart = moderateStart + moderateYears
      let growthYears = fc.futureYears.length - growthStart
      let growth
      [workingCapital, growth] = fillBucket(years, growthStart, growthYears, workingCapital);

      let assets: Asset[] = []
      
      if (bucketGroups.length > 0) {
        seriesData.push({
          name: "Needed $",
          data: [income, conservative, moderate, growth],
          group: "Needed"
        })

        bucketGroups.forEach((group: IWithdrawalGroup) => {
          group.assetIds.forEach((id: string) => {
            const asset = fc.liquidAssets.find((a: Asset) => a.id === id)
            if (asset) {
              assets.push(asset)
            }
          })
        })
      } else if (!adjustWithdrawals) {
        seriesData.push({
          name: "Allocation",
          data: [income, conservative, moderate, remainingCapital],
          group: "Needed"
        })
      }

      if (assets.length > 0) {
        const buckets: [number, number, number][] = [[1, incomeYears, income], [incomeYears + 1, conservativeYears, conservative], [incomeYears + conservativeYears + 1, moderateYears, moderate], [incomeYears + conservativeYears + moderateYears + 1, 99, 1000000000]]
        if (adjustWithdrawals) {
          const assetSeries = getAssetSeriesAdjusted(assets, fc, yearOption, buckets, undefined)
          seriesData = [...seriesData, ...assetSeries]
        } else if (isBucketStrategy) {
          const assetSeries = getAssetSeries(assets, fc, yearOption, buckets, isBucketStrategy ? bucketGroups : undefined)
          seriesData = [...seriesData, ...assetSeries]
        }
      }

      // Omit empty buckets
      if (incomeYears === 0 || conservativeYears === 0 || moderateYears === 0) {
        seriesData.forEach((series: any) => {
          if (moderateYears === 0) {
            series.data.splice(2, 1)
          }
          if (conservativeYears === 0) {
            series.data.splice(1, 1)
          }
          if (incomeYears === 0) {
            series.data.splice(0, 1)
          }
        })
        if (moderateYears === 0) {
          newOptions.xaxis.categories.splice(2, 1)
        }
        if (conservativeYears === 0) {
          newOptions.xaxis.categories.splice(1, 1)
        }
        if (incomeYears === 0) {
          newOptions.xaxis.categories.splice(0, 1)
        }
      }
    })

    setSeries(seriesData)

    if (calcs.length > 4) {
      newOptions.dataLabels.enabled = false
    } else {
      newOptions.dataLabels.style.fontSize = `${18 - 2 * calcs.length}px`
    }
    setOptions(newOptions)
  }

  const handleSetYear = (value: number) => {
    setYearOption(value)
    if (model) {
      model.settings[ModelSetting.BucketsStartYear] = value
      modelStore.saveSettings(model)
    }
  }

  const handleSetIncomeYears = (value: number) => {
    setIncomeYears(value)
    if (model) {
      model.settings[ModelSetting.BucketsIncomeYears] = value
      modelStore.saveSettings(model)
    }
  }

  const handleSetConservativeYears = (value: number) => {
    setConservativeYears(value)
    if (model) {
      model.settings[ModelSetting.BucketsConservativeYears] = value
      modelStore.saveSettings(model)
    }
  }

  const handleSetModerateYears = (value: number) => {
    setModerateYears(value)
    if (model) {
      model.settings[ModelSetting.BucketsModerateYears] = value
      modelStore.saveSettings(model)
    }
  }

  const handleSetAdjustWithdrawals = (value: boolean) => {
    setAdjustWithdrawals(value)
    if (model) {
      model.settings[ModelSetting.BucketsAdjustWithdrawals] = value
      modelStore.saveSettings(model)
    }
  }

  const handleSavePlanChange = (plan: Plan, change: PlanChange) => {
    plan.setChange(change)
    handleSavePlan(plan)    
  }

  const handleSavePlan = async (plan: Plan) => {
    // Update plan
    const updateInput: UpdatePlanInput = {
      id: plan.id
    }
    const update = await modelStore.updatePlan(updateInput)

    if (onChange && update) {
      onChange(update)
    }
  }
  
  const yearsOptions = [0, 1, 2, 3, 4, 5, 6, 7,8, 9, 10]

  if (options && series) {
    return (
      <React.Fragment>
        <TableContainer component={Paper} sx={{paddingTop:1, overflowY:"hidden", height:"calc(45vh)"}}>
          <Chart
            options={options}
            series={series}
            type="bar"
            height="100%"
          />
        </TableContainer>

          <TableContainer component={Paper} sx={{paddingTop:1, marginTop:2}}>
            <Box display="flex" flexGrow={1} flexDirection="column" px={2} pb={1}>
            <Typography variant="body1" gutterBottom>
              <b style={{color: globalColors.primary}}>Bucket Strategy</b> is designed to help you allocate your money across your retirement.
              This method of asset allocation suggests that when drawing money out of your account,
              you will want to take less risk with the money you need in the near term,
              and you can afford to take more risk with money that you won't need to tap into for several years into the future.
            </Typography>
            <FormValidator id="buckets-form" name="bucketsForm" autoComplete="off">
              <Box pt={0} pb={0} width="100%" display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" bgcolor={globalColors.details}>
                <Box width={200} pl={1} pt={1.25}>
                  <TextFieldValidator
                    type="text"
                    validators={{ required: true }}
                    required
                    name="year"
                    label="Withdrawal Start Year"
                    variant="outlined"
                    size="small"
                    sx={{maxWidth: "200px"}}
                    autocompleteOptions={{
                      freeSolo: false,
                      options: yearOptions,
                      disableClearable: true,
                      getOptionLabel: (option: number) => {
                        // return "Label"
                        return(String(option))
                      },
                      value: yearOption,
                      onChange: (event: any, value: number, reason: any) => {
                        handleSetYear(value)
                      }
                    }}
                  />
                </Box>
                <Box width="75%" pl={0.5} pt={0}>
                  <Box display="flex" flexDirection="row" justifyContent="flex-start">
                    <Typography variant="h6" textAlign="left" sx={{mr: 1}}>Withdrawal Strategy:</Typography>
                    <Link href="#" onClick={(event: any) => {setShowWithdrawalStrategyDialog(true)}}>
                      <Typography variant="h6" textAlign="left">{withdrawalStrategyType ? withdrawalStrategyType : "None"}</Typography>
                    </Link>
                    {withdrawalStrategyType !== WithdrawalStrategyType.Bucket &&
                      <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" ml={2}>
                        <Tooltip title={<Typography variant="body2">Click to enable Bucket Withdrawal Strategy to view bucket asset allocation.</Typography>}>
                          <InfoIcon sx={{mr: 0.5, color: globalColors.primary}}/>
                        </Tooltip>
                      </Box>
                    }
                  </Box>
                  <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" ml={2}>
                    <FormControlLabel control={
                      <Checkbox checked={adjustWithdrawals}
                                onChange={(event: any) => {
                                  handleSetAdjustWithdrawals(event.target.checked)
                                }}
                                sx={{margin:0, padding:0, paddingRight:1}}
                      />}
                      label="Show adjusted withdrawals"
                      sx={{ margin:0, padding:0}}
                      componentsProps={{
                        typography: {
                          variant:"body2"
                        }}
                      }
                    />
                    <Tooltip title={<Typography variant="body2">Adjust asset withdrawals to match needed $ across buckets.</Typography>}>
                      <InfoIcon sx={{ml: 2, color: globalColors.primary}}/>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            <Grid container sx={{marginTop:0}} spacing={2}>
              <Grid item sm={3}>
                <Grid container>
                  <Grid item xs={8} pt={1}>
                    <Typography variant="h3" color="primary" align="left">Income</Typography>
                  </Grid>
                  <Grid item xs={4} justifyContent="flex-end" pr={1}>
                    <TextFieldValidator
                      type="text"
                      validators={{ required: true }}
                      required
                      name="incomeYears"
                      label="Years"
                      variant="outlined"
                      size="small"
                      sx={{maxWidth: "100px"}}
                      autocompleteOptions={{
                        freeSolo: false,
                        options: yearsOptions,
                        disableClearable: true,
                        getOptionLabel: (option: number) => {
                          // return "Label"
                          return(option.toString())
                        },
                        value: incomeYears,
                        onChange: (event: any, value: number, reason: any) => {
                          handleSetIncomeYears(value)
                        }
                      }}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <Typography variant="body2">
                      <b>Purpose:</b> Low-risk funds to cover immediate living expenses and short-term needs over the next {incomeYears} years.
                    </Typography>
                    <Typography variant="body2">
                      <b>Investments:</b> Cash, money market accounts, CDs..
                    </Typography>
                    <Typography variant="body2">
                      <b>Importance:</b> This bucket provides a safety net against market volatility. Regardless of how the market performs, retirees have liquid assets available to support their essential expenses.
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item sm={3}>
                <Grid container>
                  <Grid item xs={8} pt={1}>
                    <Typography variant="h3" color="primary" align="left">Conservative</Typography>
                  </Grid>
                  <Grid item xs={4} justifyContent="flex-end" pr={1}>
                    <TextFieldValidator
                      type="text"
                      validators={{ required: true }}
                      required
                      name="conserveYears"
                      label="Years"
                      variant="outlined"
                      size="small"
                      sx={{maxWidth: "100px"}}
                      autocompleteOptions={{
                        freeSolo: false,
                        options: yearsOptions,
                        disableClearable: true,
                        getOptionLabel: (option: number) => {
                          // return "Label"
                          return(option.toString())
                        },
                        value: conservativeYears,
                        onChange: (event: any, value: number, reason: any) => {
                          handleSetConservativeYears(value)
                        }
                      }}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <Typography variant="body2">
                      <b>Purpose:</b> Used after the income bucket is depleted for the next {conservativeYears} years. Focus is on stability and capital preservation, not growth.
                    </Typography>
                    <Typography variant="body2">
                      <b>Investments:</b> Diversified mix with more bonds and fewer stocks (e.g., 60% bonds / 40% stocks).
                    </Typography>
                    <Typography variant="body2">
                      <b>Importance:</b> This bucket offers stability, aiming to conserve assets, yet keep up with inflation.
                    </Typography>
                  </Grid>
                </Grid>              </Grid>
              <Grid item sm={3}>
                <Grid container>
                  <Grid item xs={8} pt={1}>
                    <Typography variant="h3" color="primary" align="left">Moderate</Typography>
                  </Grid>
                  <Grid item xs={4} justifyContent="flex-end" pr={1}>
                    <TextFieldValidator
                      type="text"
                      validators={{ required: true }}
                      required
                      name="conserveYears"
                      label="Years"
                      variant="outlined"
                      size="small"
                      sx={{maxWidth: "100px"}}
                      autocompleteOptions={{
                        freeSolo: false,
                        options: yearsOptions,
                        disableClearable: true,
                        getOptionLabel: (option: number) => {
                          // return "Label"
                          return(option.toString())
                        },
                        value: moderateYears,
                        onChange: (event: any, value: number, reason: any) => {
                          handleSetModerateYears(value)
                        }
                      }}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <Typography variant="body2">
                      <b>Purpose:</b> Funds needed for the next {moderateYears} years. Balanced for some growth, income, and preservation.
                    </Typography>
                    <Typography variant="body2">
                      <b>Investments:</b> Moderate risk allocation (e.g., 60% stocks / 40% bonds).
                    </Typography>
                    <Typography variant="body2">
                      <b>Importance:</b> This bucket offers a blend of growth and stability, aiming to outpace inflation while maintaining less volatility than stocks.
                    </Typography>
                  </Grid>
                </Grid>                 </Grid>
              <Grid item sm={3}>
                <Typography variant="h3" color="primary" align="left" pt={1} height={51}>Growth</Typography>
                <Typography variant="body2">
                  <b>Purpose:</b> Long-term funds, not needed for {incomeYears + conservativeYears + moderateYears + 1} years. Focused on growth, with greater tolerance for market volatility.
                </Typography>
                <Typography variant="body2">
                  <b>Investments:</b> Higher stock allocation (e.g., 70% stocks / 30% bonds).
                </Typography>
                <Typography variant="body2">
                  <b>Importance:</b> This bucket is designed for long-term growth, capitalizing on the ability to take greater risks with a longer time horizon. It helps ensure that retirees don't outlive their savings by providing steady growth over time.
                </Typography>
              </Grid>
            </Grid>
            </FormValidator>
            </Box>
          </TableContainer>
          {showWithdrawalStrategyDialog && model &&
          <WithdrawalStrategyDialog plan={plan!} model={model!}
                                    open={showWithdrawalStrategyDialog}
                                    onSave={(plan: Plan, update: PlanChange) => {
                                      setShowWithdrawalStrategyDialog(false)
                                      handleSavePlanChange(plan, update)
                                    }}
                                    onClose={() => {
                                      setShowWithdrawalStrategyDialog(false)
                                    }}/>
        }
      </React.Fragment>
    )
  } else {
    return null
  }
}

export default BucketsChart